// ════════════════════════════════════════════════════════════════════════════
//  features/fiscal/hooks/useNfce.ts
// ════════════════════════════════════════════════════════════════════════════

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { nfceService } from '../services/nfceService';
import type { EmitirNfceRequest } from '../types';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';

const KEYS = {
  all:       ['nfce'] as const,
  lista:     (p: number, t: number, ini?: string, fim?: string) =>
    ['nfce', 'lista', p, t, ini ?? '', fim ?? ''] as const,
  detalhe:   (id: number) => ['nfce', id] as const,
  proximoN:  ['nfce', 'proximo-numero'] as const,
};

export const useNfceList = (
  pagina = 1,
  tamanho = 30,
  inicio?: string,
  fim?: string,
) => {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: KEYS.lista(pagina, tamanho, inicio, fim),
    queryFn:  () => nfceService.listar(pagina, tamanho, inicio, fim),
  });

  const cancelarMutation = useMutation({
    mutationFn: ({ id, justificativa }: { id: number; justificativa: string }) =>
      nfceService.cancelar(id, justificativa),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      if (res.sucesso) toast.success('NFC-e cancelada na SEFAZ.');
      else toast.warning(`Cancelamento: ${res.xMotivo}`);
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return {
    ...query,
    notas:           query.data?.items ?? [],
    total:           query.data?.total ?? 0,
    cancelar:        cancelarMutation.mutate,
    isCancelando:    cancelarMutation.isPending,
  };
};

export const useNfceDetalhe = (id: number) =>
  useQuery({
    queryKey: KEYS.detalhe(id),
    queryFn:  () => nfceService.detalhe(id),
    enabled:  id > 0,
  });

export const useProximoNumeroNfce = () =>
  useQuery({
    queryKey: KEYS.proximoN,
    queryFn:  () => nfceService.proximoNumero(),
  });

export const useEmitirNfce = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: EmitirNfceRequest) => nfceService.emitir(req),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      if (res.sucesso) {
        toast.success(`NFC-e ${res.numeroNota} autorizada (${res.cStat}).`);
      } else {
        toast.warning(`Rejeitada: ${res.xMotivo}`);
      }
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
};

/** Baixa o DANFE-NFCe e dispara o download/preview no browser. */
export const useObterDanfeNfce = () =>
  useMutation({
    mutationFn: (id: number) => nfceService.obterDanfe(id),
    onSuccess: (blob, id) => {
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoga o URL depois de 60s para liberar memória
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      void id;
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
